<template>
  <section
    :class="[readOnly ? 'is-disabled' : '']"
    data-cy="property-tab"
    :data-view-ready="loadingFinish">
    <coozzy-card class="global-card no-top-border">
      <div
        v-b-toggle.collapse-baseData
        class="collapse-title"
        data-cy="section-property-base-data">
        <h5 class="cursor-pointer">
          {{ $t('message.onBoarding.createBuilding.baseData') }}
        </h5>
      </div>
      <div class="col-12 pl-0">
        <b-collapse
          id="collapse-baseData"
          visible>
          <div class="row">
            <div class="col-sm-12 col-md-9">
              <div class="row details-row-bg-1 mt-3 py-2">
                <div class="col-sm-12 col-md-4">
                  <coozzy-form-input
                    v-model="property.name"
                    data-cy="input-property-name"
                    :is-read-only="readOnly"
                    :disabled="isJanitor"
                    :state="$v.property.name.$dirty && $v.property.name.$error ? false : null"
                    :limit="50"
                    :name="$t('message.propertiesView.name') + (!readOnly ? '*' : '')" />
                </div>
                <div class="col-sm-12 col-md-4">
                  <coozzy-form-input
                    v-model="numericId"
                    data-cy="input-property-id"
                    :disabled="true"
                    :is-read-only="readOnly"
                    :name="$t('message.propertiesView.number')" />
                </div>
                <div class="col-sm-12 col-md-4">
                  <coozzy-form-country-select
                    v-model="property.address.country"
                    data-cy="select-property-country"
                    :initial-value="property.address.country"
                    :is-read-only="readOnly"
                    :disabled="isJanitor" />
                </div>
              </div>
              <div class="row details-row-bg-2 py-01rem">
                <div class="col-sm-12 col-md-4">
                  <coozzy-form-input
                    v-model="property.address.zip"
                    data-cy="input-property-zip"
                    :is-read-only="readOnly"
                    type="text"
                    :prevent-letters="property.address.country === 'DE' || property.address.country === 'CH'"
                    :limit="zipLength"
                    :state="$v.property.address.zip.$dirty && $v.property.address.zip.$error ? false : null"
                    :name="$t('message.generic.zip') + (!readOnly ? '*' : '')"
                    :disabled="isJanitor"
                    @input="getCityFromZip" />
                </div>
                <div class="col-sm-12 col-md-4">
                  <coozzy-form-input
                    v-if="!isCitiesSuggested"
                    v-model="property.address.city"
                    data-cy="input-property-city"
                    :is-read-only="readOnly"
                    :disabled="isJanitor"
                    :name="$t('message.generic.city') + (!readOnly ? '*' : '')" />
                  <template v-else>
                    <label for="type">{{ $t('message.generic.city') }}*</label>
                    <coozzy-form-select
                      id="city"
                      data-cy="select-property-city"
                      :state="$v.property.address.city.$dirty && $v.property.address.city.$error ? false : null"
                      :disabled="isJanitor"
                      @change="cityChanged">
                      <option
                        v-for="(item, typeIndex) in citiesValue"
                        :key="typeIndex"
                        :value="item">
                        {{ item.city }}
                      </option>
                    </coozzy-form-select>
                  </template>
                </div>
                <div class="col-sm-12 col-md-4">
                  <coozzy-form-region-select
                    v-model="property.address.state"
                    data-cy="select-property-region"
                    :initial-value="property.address.state"
                    :is-read-only="readOnly"
                    :disabled="isJanitor"
                    :country="property.address.country" />
                </div>
              </div>
              <div class="row details-row-bg-1 py-01rem">
                <div class="col-sm-12 col-md-4">
                  <label for="type">{{ $t('message.propertiesView.propertyType.title') }}{{ (!readOnly ? '*' : '') }}</label>
                  <coozzy-form-select
                    v-if="!readOnly"
                    id="type"
                    v-model="property.type"
                    data-cy="select-property-type"
                    :disabled="property.type !== 'PROPERTY_TYPE_UNDEFINED' || isJanitor">
                    <option
                      value="">
                      -
                    </option>
                    <option
                      v-for="(type, typeIndex) in typeValue"
                      :key="typeIndex"
                      :value="type">
                      {{ $t(`message.propertiesView.propertyType.${type}`) }}
                    </option>
                  </coozzy-form-select>
                  <span
                    v-else
                    class="show-data">{{ property.type === '' || property.type === null || property.type === 'PROPERTY_TYPE_UNDEFINED' ? '-' : $t(`message.propertiesView.propertyType.${property.type}`) }}</span>
                </div>
                <div class="col-sm-12 col-md-4">
                  <label for="development">{{ $t('message.propertiesView.development.title') }}</label>
                  <coozzy-form-select
                    v-if="!readOnly"
                    id="development"
                    v-model="property.development"
                    data-cy="select-property-development"
                    :disabled="isJanitor">
                    <option value="PROPERTY_DEVELOPMENT_UNDEFINED" />
                    <option value="PROPERTY_DEVELOPMENT_FULL">
                      {{ $t('message.propertiesView.development.PROPERTY_DEVELOPMENT_FULL') }}
                    </option>
                    <option value="PROPERTY_DEVELOPMENT_PARTIAL">
                      {{ $t('message.propertiesView.development.PROPERTY_DEVELOPMENT_PARTIAL') }}
                    </option>
                    <option value="PROPERTY_DEVELOPMENT_UNDEVELOPED">
                      {{ $t('message.propertiesView.development.PROPERTY_DEVELOPMENT_UNDEVELOPED') }}
                    </option>
                  </coozzy-form-select>
                  <span
                    v-else
                    class="show-data">
                    {{ property.development === '' || property.development === null || property.development === 'PROPERTY_DEVELOPMENT_UNDEFINED' ? '-' : $t(`message.propertiesView.development.${property.development}`) }}
                  </span>
                </div>
                <div
                  class="col-sm-12 col-md-4">
                  <div class="row">
                    <div
                      v-if="property.type === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP'"
                      class="col-6">
                      <coozzy-form-input
                        v-model="totalPropertyValueQuota"
                        data-cy="input-property-value-quota"
                        :is-read-only="readOnly"
                        :type="'number'"
                        :name="$t('message.tableColumnHeadings.totalValueQuota')"
                        :disabled="true" />
                    </div>
                    <div
                      :class="[property.type === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP' ? 'col-6' : 'col-12']">
                      <label for="keyDate"> {{ $t('message.propertiesView.keyDate') }} </label>
                      <date-picker
                        v-if="!readOnly"
                        ref="datepicker"
                        v-model="dates.keyDateServiceChargeSettlement"
                        value-type="format"
                        format="DD.MM"
                        type="day"
                        class="w-100"
                        @clear="clearkeyDateServiceChargeSettlementDate"
                        @change="handleKeyDateChange">
                        <template #input>
                          <b-form-input
                            v-model="keyDateService"
                            data-cy="input-property-key-date"
                            :class="'form-control form-control-sm'"
                            type="text"
                            size="sm"
                            :disabled="readOnly"
                            :placeholder="'dd.mm'"
                            @keydown="checkInputValue($event)"
                            @input="mountEventListenersKeyDate" />
                        </template>
                      </date-picker>
                      <span
                        v-else
                        class="d-block">
                        {{ keyDateService }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row details-row-bg-2 py-01rem">
                <div
                  class="col-sm-12 col-md-4 mt-2">
                  <coozzy-form-checkbox
                    v-if="!readOnly"
                    id="opted"
                    v-model="property.opted"
                    data-cy="checkbox-property-opted"
                    :initial="property.opted"
                    :is-read-only="readOnly"
                    :disabled="isJanitor"
                    :state="$v.property.opted.$dirty && $v.property.opted.$error ? false : null"
                    class="align-self-center mt-3">
                    {{ $t('message.onBoarding.buildings.objects.opted') }}
                  </coozzy-form-checkbox>
                  <div
                    v-else>
                    <label>{{ $t('message.onBoarding.buildings.objects.opted') }}</label>
                    <span class="show-data">
                      {{ property.opted ? $t('message.generic.selectField.yes') : $t('message.generic.selectField.no') }}
                    </span>
                  </div>
                </div>
                <div
                  class="col-sm-12 col-md-4">
                  <label for="optedPortionKey">{{ $t('message.propertiesView.optedPortionKey.title') }} {{ (!readOnly && property.opted ? '*' : '') }}</label>
                  <coozzy-form-select
                    v-if="!readOnly"
                    id="optedPortionKey"
                    v-model="property.optedPortionKey"
                    data-cy="select-property-opted-portion-key"
                    :disabled="!property.opted || isJanitor"
                    :state="property.opted && $v.property.optedPortionKey.$dirty && $v.property.optedPortionKey.$error ? false : null">
                    <option value="">
                      -
                    </option>
                    <option
                      v-for="(optedPortionKeyItem, index) in optedPortionKeyValue"
                      :key="index"
                      :value="optedPortionKeyItem">
                      {{ $t(`message.propertiesView.optedPortionKey.${optedPortionKeyItem}`) }}
                    </option>
                  </coozzy-form-select>
                  <span
                    v-else
                    class="show-data">{{ property.optedPortionKey === '' || property.optedPortionKey === null || property.optedPortionKey === 'OPTED_PORTION_KEY_UNDEFINED' ? '-' : $t(`message.propertiesView.optedPortionKey.${property.optedPortionKey}`) }}</span>
                </div>

                <div class="col-sm-12 col-md-4">
                  <label for="note">{{ $t('message.tableColumnHeadings.assignee') }}</label>
                  <coozzy-form-select
                    v-if="!readOnly"
                    v-model="propertyAssignee"
                    data-cy="select-property-assignee"
                    :disabled="isJanitor">
                    <option :value="''">
                      -
                    </option>
                    <option
                      v-for="element in listEmployees"
                      :key="element.id"
                      :value="element.id">
                      <template>
                        {{ element.firstName }} {{ element.lastName }}
                      </template>
                    </option>
                  </coozzy-form-select>
                  <span
                    v-else
                    class="show-data">{{ getAssigneeName() }}</span>
                </div>
                <div class="col-sm-12 col-md-4">
                  <label for="note">{{ $t('message.generic.note') }}</label>
                  <coozzy-form-textarea
                    ref="note"
                    v-model="property.note"
                    data-cy="textarea-property-note"
                    :initial="property.note"
                    :value-text-area="property.note"
                    :show-label="false"
                    :is-read-only="readOnly"
                    :disabled="isJanitor"
                    name="note" />
                </div>
              </div>
            </div>
            <div class="d-flex col-sm-12 col-md-3 mt-3 mt-md-0 justify-content-center">
              <coozzy-spinner
                v-if="mediaUploadProcessing"
                class="align-self-center" />
              <div
                v-if="!mediaUploadProcessing && propertyImageMedia"
                data-cy="img-property-thumbnail"
                class="uploaded-file my-2 w-100"
                :class="[readOnly ? 'cursor-pointer' : '']"
                style="background-repeat: no-repeat;background-size: cover;background-position: center center; max-height: 260px;"
                :style="{ 'background-image': 'url(' + propertyImageMedia.url + ')' }"
                :draggable="true"
                @dragstart="handleDragStart(propertyImageMedia.id)"
                @click="showImage">
                <div
                  data-cy="btn-property-thumbnail-delete"
                  :class="['object-image-remove-container', 'cursor-pointer', readOnly || isJanitor ? 'd-none' : '']"
                  @click="deleteMedia()">
                  <coozzy-close-icon class="object-image-remove bg" />
                  <coozzy-close-icon class="object-image-remove" />
                </div>
              </div>
              <img
                v-if="!mediaUploadProcessing && !propertyImageMedia"
                data-cy="btn-property-thumbnail-upload"
                :class="['align-self-center', 'building-image', readOnly || isJanitor ? '' : 'cursor-pointer']"
                src="/img/image-placeholder.png"
                alt="Placeholder"
                @click="uploadImageClicked">

              <coozzy-form-file-input
                v-if="!readOnly && !isJanitor"
                ref="mediaUpload"
                data-cy="input-property-thumbnail"
                class="d-none"
                accept="image/png,image/jpeg,image/jpg"
                @change="addFile" />
            </div>
          </div>
        </b-collapse>
      </div>
    </coozzy-card>
    <coozzy-card
      v-if="!isJanitor"
      class="global-card">
      <property-rent-components
        :read-only="readOnly"
        :property-selected="property"
        :expand-by-default="false"
        @has-change="$emit('has-change', true)" />
    </coozzy-card>
    <coozzy-card class="global-card">
      <div
        v-b-toggle.collapse-property-device
        class="collapse-title"
        data-cy="section-property-devices">
        <h5 class="cursor-pointer">
          {{ $t('message.onBoarding.createBuilding.device') }}
          <template v-if="devicesLoading">
            <coozzy-spinner :small="true" />
          </template>
          <template v-else>
            ({{ devices.length }})
          </template>
        </h5>
      </div>
      <div
        class="col-12 p-0"
        data-cy="property-devices-panel"
        :data-view-ready="!devicesLoading && (!openedCollapses.includes('collapse-property-device') || (renderDevices && !renderDevicesAnimation))">
        <b-collapse
          id="collapse-property-device"
          :visible="openedCollapses.includes('collapse-property-device')"
          @show="renderDevicesAnimation=true"
          @shown="renderDevices=true"
          @hidden="renderDevices = false">
          <div
            v-if="devices.length === 0 && readOnly"
            class="row mt-3">
            <div class="col-12">
              <h6 data-cy="property-devices-empty">
                {{ $t('message.nonExistent.device') }}
              </h6>
            </div>
          </div>
          <template v-if="devices.length > 0 && renderDevicesAnimation === true">
            <coozzy-spinner />
          </template>
          <template v-if="renderDevices">
            <div
              v-for="(device, deviceIndex) in devices"
              :key="`devices-${device.id || device.internalId}`"
              class="row">
              <device
                :key="`device-i-${device.id || device.internalId}`"
                ref="property-device"
                :index="deviceIndex"
                :read-only="readOnly"
                :device="device"
                :target="'property'"
                :external-contact-list="propertyOwnerContacts"
                :owner-id="property?.ownerId"
                :expand-by-default="(('isNewItem' in device) && ((deviceIndex + 1) === devices.length))"
                :add-new-document="device.id !== '' && deviceIdNewDocument !== '' && deviceIdNewDocument === device.id"
                @removeConfirmed="removeDevice(deviceIndex)"
                @device-update="$emit('has-change', true)"
                @duplicateDevice="duplicateDevice"
                @selected-device-for-history="openActivities" />
            </div>
          </template>
          <div class="row">
            <div class="col-2 mt-1">
              <coozzy-button
                v-if="!readOnly"
                class="w-100"
                design="prop-green"
                data-cy="btn-property-device-add"
                @click="addNewDevice">
                {{ $t('message.onBoarding.devices.newDevice') }}
              </coozzy-button>
            </div>
          </div>
        </b-collapse>
      </div>
    </coozzy-card>
    <coozzy-card
      v-if="!isJanitor"
      class="global-card position-relative">
      <div
        v-b-toggle.collapse-property-insurance
        class="collapse-title position-relative"
        data-cy="section-property-insurance">
        <h5 class="cursor-pointer">
          {{ $t('message.onBoarding.createBuilding.insurance') }}
          <template v-if="insurancesLoading">
            <coozzy-spinner :small="true" />
          </template>
          <template v-else>
            ({{ propertyInsurances.length }})
          </template>
        </h5>
        <div
          v-if="property && propertyInsuranceCollapseOpen && isPropertyAicSynced([property])"
          class="position-absolute"
          :style="{ right: '80px', top: '0px' }"
          @click.stop>
          <coozzy-dropdown
            :text="$t('message.generic.actions')"
            design="default"
            size="sm"
            class="w-20 sort"
            :disabled="constructionInsuranceLoading">
            <coozzy-dropdown-item @click.native.prevent.stop="openConstructionInsuranceInAic(property)">
              {{ $t('message.onBoarding.buildings.insurances.aic.constructionInsurance') }}
            </coozzy-dropdown-item>
          </coozzy-dropdown>
        </div>
      </div>
      <div class="col-12 p-0">
        <b-collapse
          id="collapse-property-insurance"
          :visible="openedCollapses.includes('collapse-property-insurance') || expandPropertyInsuranceId !== ''"
          @shown="propertyInsuranceCollapseOpen = true"
          @hidden="propertyInsuranceCollapseOpen = false">
          <div
            v-if="propertyInsurances.length === 0 && readOnly"
            class="row mt-3">
            <div class="col-12">
              <h6>
                {{ $t('message.nonExistent.insurance') }}
              </h6>
            </div>
          </div>
          <div
            v-for="(insurance, insuranceIndex) in propertyInsurances"
            :key="`insurance-${insurance.id || insurance.internalId}`"
            class="row">
            <building-insurance
              :id="`property_insurance_${insurance.id || insurance.internalId}`"
              :key="`insurance-i-${insurance.id || insurance.internalId}`"
              ref="property-insurance"
              :index="insuranceIndex"
              :read-only="readOnly"
              :insurance="insurance"
              :target="'property'"
              :expand-by-default="(('isNewItem' in insurance) && ((insuranceIndex + 1) === propertyInsurances.length)) || expandPropertyInsuranceId === insurance.id"
              :add-new-document="insurance.id && insuranceIdNewDocument !== '' && insuranceIdNewDocument === insurance.id"
              :synced-to-aic="isPropertyAicSynced([property])"
              @removeConfirmed="removeInsurance(insurance.id || insurance.internalId)"
              @change-insurance="$emit('has-change', true)" />
          </div>
          <div class="row">
            <div class="col-2 mt-1">
              <coozzy-button
                v-if="!readOnly"
                class="w-100"
                design="prop-green"
                data-cy="btn-property-insurance-add"
                @click="addNewInsurance">
                {{ $t('message.onBoarding.buildings.insurances.add') }}
              </coozzy-button>
            </div>
          </div>
        </b-collapse>
      </div>
    </coozzy-card>
    <coozzy-card
      v-if="!isJanitor"
      class="global-card">
      <div
        v-b-toggle.collapse-property-mortgages
        class="collapse-title"
        data-cy="section-property-mortgages">
        <h5 class="cursor-pointer">
          {{ $t('message.onBoarding.createBuilding.mortgage') }}
          <template v-if="mortgagesLoading">
            <coozzy-spinner :small="true" />
          </template>
          <template v-else>
            ({{ mortgages.length }})
          </template>
        </h5>
      </div>
      <div class="col-12 p-0">
        <b-collapse
          id="collapse-property-mortgages"
          ref="collapseMortgage"
          :visible="openedCollapses.includes('collapse-property-mortgages') || expandPropertyMortgageId !== ''">
          <template v-if="mortgagesLoading">
            <coozzy-spinner />
          </template>
          <div
            v-else-if="mortgages.length === 0 && readOnly"
            class="row mt-3">
            <div class="col-12">
              <h6>
                {{ $t('message.nonExistent.mortgage') }}
              </h6>
            </div>
          </div>
          <div
            v-for="(item, index) in mortgages"
            :key="`mortgages-${item.id || item.internalId}`"
            class="row">
            <mortgage
              :id="'property_mortgage_' + item.id || item.internalId"
              ref="property-mortgage"
              :key="`mortgage-${item.id || item.internalId}`"
              :index="index"
              :read-only="readOnly"
              :mortgage="item"
              :expand-by-default="('isNewItem' in item) && ((index + 1) === mortgages.length) || expandPropertyMortgageId === item.id"
              :add-new-document="item.id && mortgageIdNewDocument !== '' && mortgageIdNewDocument === item.id"
              @remove-mortgage="removeMortgage"
              @update-mortgage="updateMortgage" />
          </div>
          <div class="row">
            <div class="col-2 mt-1">
              <coozzy-button
                v-if="!readOnly"
                class="w-100"
                design="prop-green"
                data-cy="btn-property-mortgage-add"
                @click="addNewMortgage">
                {{ $t('message.onBoarding.mortgage.newMortgage') }}
              </coozzy-button>
            </div>
          </div>
        </b-collapse>
      </div>
    </coozzy-card>
    <coozzy-card
      v-if="!isJanitor"
      class="global-card">
      <div
        v-b-toggle.collapse-property-bank-details
        class="collapse-title"
        data-cy="section-property-bank-details">
        <h5 class="cursor-pointer">
          {{ $t('message.onBoarding.createBuilding.propertyBankDetails') }}
          <template v-if="bankDetailsLoading">
            <coozzy-spinner :small="true" />
          </template>
          <template v-else>
            ({{ bankDetails.length }})
          </template>
        </h5>
      </div>
      <div
        class="col-12 p-0"
        data-cy="property-bank-details-panel"
        :data-view-ready="!bankDetailsLoading">
        <b-collapse
          id="collapse-property-bank-details"
          ref="collapseBankDetails"
          :visible="openedCollapses.includes('collapse-property-bank-details')">
          <template v-if="bankDetailsLoading">
            <coozzy-spinner />
          </template>
          <div
            v-else-if="bankDetails.length === 0 && readOnly"
            class="row mt-3">
            <div class="col-12">
              <h6 data-cy="property-bank-details-empty">
                {{ $t('message.nonExistent.bankDetails') }}
              </h6>
            </div>
          </div>
          <div
            v-for="(item, index) in bankDetails"
            :key="`bank-details-${index}`"
            class="row">
            <property-bank-details
              ref="property-bank-details"
              :key="`bank-details-${index}`"
              :owner-id="property.ownerId"
              :index="index"
              :read-only="readOnly"
              :bank-details="item"
              :expand-by-default="('isNewItem' in item) && ((index + 1) === bankDetails.length)"
              :add-new-document="propBankIndexNewDocument === index"
              @remove-bank-details="removeBankDetails"
              @update-bank-details="updateBankDetails" />
          </div>
          <div class="row">
            <div class="col-2 mt-1">
              <coozzy-button
                v-if="!readOnly"
                class="w-100"
                design="prop-green"
                data-cy="btn-property-bank-details-add"
                @click="addNewBankDetails">
                {{ $t('message.onBoarding.bankDetails.newBankDetails') }}
              </coozzy-button>
            </div>
          </div>
        </b-collapse>
      </div>
    </coozzy-card>
    <coozzy-card
      v-if="!isJanitor"
      class="global-card">
      <div
        v-b-toggle.collapse-property-document
        class="collapse-title"
        data-cy="section-property-documents">
        <h5 class="cursor-pointer">
          {{ $t('message.propertiesView.documents') }} ({{ propertyDocuments.length }})
        </h5>
      </div>
      <div
        class="col-12 p-0"
        data-cy="property-documents-panel">
        <b-collapse
          id="collapse-property-document"
          :visible="openedCollapses.includes('collapse-property-document')">
          <span
            v-if="propertyDocuments.length > 0"
            class="link downloadAllDocuments"
            data-cy="btn-property-documents-download-all"
            @click="downloadAllDocuments($event)">{{ $t('message.marketingMessages.mail.downloadAll') }} <coozzy-file-download-icon /></span>
          <documents-list
            :documents-list="propertyDocuments"
            :is-read-only="readOnly"
            @version-added="versionAdded"
            @document-deleted="deleteDocument" />
          <div class="row">
            <add-document-modal
              :owner-id="property.ownerId"
              @document-created="documentCreated" />
            <div
              v-if="!userWithoutRole"
              class="col-2 mt-1">
              <coozzy-button
                class="w-100"
                design="prop-green"
                data-cy="btn-property-document-add"
                @click="readOnly ? goToEditMode() : addDocumentClicked()">
                {{ $t('message.manageDocuments.addDocument') }}
              </coozzy-button>
            </div>
          </div>
        </b-collapse>
      </div>
    </coozzy-card>
  </section>
</template>

<script>
import CoozzyCard from '../../framework/components/card/CoozzyCard'
import Mortgage from '@/properties/components/Mortgage'
import PropertyBankDetails from '@/properties/components/PropertyBankDetails'
import Device from '@/building/tabs/components/Device'
import BuildingInsurance from '@/building/tabs/components/BuildingInsurance'
import AddDocumentModal from '@/properties/components/AddDocumentModal'
import CoozzyFormInput from '../../framework/components/form/input/CoozzyFormInput'
import CoozzyFormSelect from '../../framework/components/form/select/CoozzyFormSelect'
import CoozzyButton from '../../framework/components/button/CoozzyButton'
import CoozzyFormFileInput from '../../framework/components/form/fileInput/CoozzyFormFileInput'
import MediaApi from '../../misc/apis/MediaApi'
import ObjectApi from '../../misc/apis/ObjectApi'
import ContactApi from '../../misc/apis/ContactApi'
import CoozzySpinner from '../../framework/components/misc/CoozzySpinner'
import Vue from 'vue'
import { decimal, maxLength, minValue, required } from 'vuelidate/lib/validators'
import CoozzyCloseIcon from '@/framework/components/icons/CoozzyCloseIcon'
import CoozzyFormCountrySelect from '@/framework/components/form/select/CoozzyFormCountrySelect'
import CoozzyFormRegionSelect from '@/framework/components/form/select/CoozzyFormRegionSelect'
import PropertyRentComponents from '../onboarding/PropertyRentComponents.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox'
import { isZipValid, validation } from '@/mixins/validation'
import { sort } from '@/mixins/sort'
import { toastError } from '@/mixins/toastError'
import { onboarding } from '@/mixins/onboarding'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea'
import { media } from '@/mixins/media'
import { routeChecks } from '@/mixins/routeChecks'
import { user } from '@/mixins/user'
import DocumentsList from '@/properties/components/DocumentsList'
import CoozzyFileDownloadIcon from '@/framework/components/icons/CoozzyFileDownloadIcon.vue'
import { address } from '@/mixins/address'
import { formatting } from '@/mixins/formatting'
import { emptyMortgage, helper } from '@/types/helper'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/de'
import { dateUtils } from '@/mixins/dateUtils'
import { aic } from '@/mixins/aic'

function copyObject(object) {
  const objCopy = {} // objCopy will store a copy of the mainObj
  let key

  for (key in object) {
    // noinspection JSUnfilteredForInLoop
    objCopy[key] = object[key] // copies each property to the objCopy object
  }
  return objCopy
}

export default {
  name: 'PropertyTab',
  components: {
    DatePicker,
    CoozzyFileDownloadIcon,
    CoozzyFormTextarea,
    CoozzyFormCheckbox,
    CoozzyFormRegionSelect,
    CoozzyFormCountrySelect,
    CoozzyCloseIcon,
    CoozzySpinner,
    CoozzyFormFileInput,
    CoozzyButton,
    CoozzyFormSelect,
    CoozzyFormInput,
    CoozzyCard,
    AddDocumentModal,
    Mortgage,
    PropertyBankDetails,
    Device,
    BuildingInsurance,
    PropertyRentComponents,
    DocumentsList
  },
  mixins: [validation, toastError, onboarding, sort, media, user, routeChecks, address, formatting, dateUtils, aic],
  beforeRouteLeave (to, from, next) {
    this.$store.dispatch('onboarding/setOpenedCollapses')
    next()
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false
    },
    propertySelected: {
      type: Object,
      default: null
    },
    expandPropertyMortgageId: {
      type: String,
      default: ''
    },
    expandPropertyInsuranceId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dates: {
        keyDateServiceChargeSettlement: ''
      },
      isCitiesSuggested: false,
      citiesValue: [],
      property: this.propertySelected,
      mediaUpload: null,
      mediaUploadProcessing: false,
      propertyImageMedia: null,
      renderDevices: false,
      renderDevicesAnimation: true,
      devicesLoading: true,
      mortgagesLoading: true,
      bankDetailsLoading: false,
      insurancesLoading: true,
      propertyInsuranceCollapseOpen: false,
      propertyDocuments: [],
      galleryImages: [],
      galleryIndex: null,
      optedPortionKeyValue: ['OPTED_PORTION_KEY_M2', 'OPTED_PORTION_KEY_M3', 'OPTED_PORTION_KEY_VALUE_RATIO', 'OPTED_PORTION_KEY_TV', 'OPTED_PORTION_KEY_ELEVATOR', 'OPTED_PORTION_KEY_LOCATION_COMPENSATION', 'OPTED_PORTION_KEY_UNIT', 'OPTED_PORTION_KEY_NUMBER_OF_INHABITANTS'],
      typeValue: ['PROPERTY_TYPE_RENTAL', 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP', 'PROPERTY_TYPE_COOPERATIVE'],
      availableTypes: [
        // Changes need to be implemented also in mobile apps
        'OTHER',
        'APPLICATION_FORM',
        'HANDOVER_PROTOCOL',
        'CONTRACT',
        'BUILDING_PLAN',
        'INVOICE',
        'HOUSE_REGULATION',
        'INSTRUCTION_MANUAL',
        'CREDIT_CHECK',
        'OWNERS_MEETING',
        'PAY_IN_SLIP',
        'ENERGY_CERTIFICATE',
        'WARRANTY_CERTIFICATE',
        'BANK_STATEMENT',
        'ADDITIONAL_COSTS_BILL',
        'SEPA_DIRECT_DEBIT_MANDATE',
        'AGREEMENT',
        'DEPOSIT_CONFIRMATION',
        'BUILDING_INSURANCE_POLICY',
        'KEY_LIST',
        'TERMINATION_DOC_TYPE',
        'RENT_ADJUSTMENT_DOC_TYPE',
        'REFERENCES_DOC_TYPE',
        'MAINTENANCE_CONTRACT_DOC_TYPE',
        'REGULATIONS',
        'REGULATIONS_FOR_USE_AND_MANAGEMENT',
        'ANNUAL_FINANCIAL_STATEMENT',
        'PURCHASE_CONTRACT',
        'LAND_REGISTER',
        'CONDOMINIUM_OWNERSHIP',
        'RENEWAL_FOND',
        'MORTGAGE',
        'ORDER',
        'OFFER',
        'IDENTITY',
        'EXTRACT_FROM_DEBT_COLLECTION_REGISTER',
        'BANK_ACCOUNT_DETAILS',
        'SITUATION_PLAN',
        'CONSTRUCTION_LAW_AGREEMENT',
        'EASEMENT_AGREEMENT',
        'CADASTRAL_ESTIMATE',
        'LETTER',
        'TRADE_REGISTER_EXCERPT',
        'PAYMENT_REMINDER',
        'PROTOCOL',
        'COURT_DOC_TYPE',
        'HONORARIUM',
        'BUDGET',
        'OFFICIAL_ESTIMATE',
        'BUILDING_DESCRIPTION',
        'PERMITS',
        'LEGAL_MATTERS',
        'LOSS_RATIO',
        'SHARE_CERTIFICATE_DOCUMENT'
      ],
      mortgageDocuments: [],
      mortgages: [],
      bankDetails: [],
      deletedMortgageIds: [],
      deletedBankDetailsIds: [],
      devices: [],
      propertyInsurances: [],
      insurancesDocuments: [],
      propertyOwnerContacts: [],
      initRentComponents: null,
      deviceIdNewDocument: '',
      mortgageIdNewDocument: '',
      insuranceIdNewDocument: '',
      propBankIndexNewDocument: '',
      buildingList: [],
      valuesQuota: [],
      listEmployees: []
    }
  },
  computed: {
    zipLength() {
      if (this.property) {
        const country = this.property.address.country
        if (country === 'CH') {
          return 4
        } else if (country === 'DE') {
          return 5
        } else {
          return 15
        }
      }
      return -1
    },
    loadingFinish() {
      return !this.devicesLoading && !this.mortgagesLoading && !this.insurancesLoading
    },
    numericId() {
      return this.property.numericId
    },
    keyDateService: {
      get() {
        return this.dates.keyDateServiceChargeSettlement ? this.dates.keyDateServiceChargeSettlement : ''
      },
      set(value) {
        this.dates.keyDateServiceChargeSettlement = value

        if (value && value.length === 5 && value.includes('.')) {
          this.handleKeyDateChange()
        } else if (!value) {
          this.property.keyDateServiceChargeSettlement = null
        }
      }
    },
    plotArea: {
      get() {
        return this.property.plotArea !== -1 ? this.property.plotArea : null
      },
      set(value) {
        this.property.plotArea = value
      }
    },
    landRegisterNumber: {
      get() {
        return this.property.landRegisterNumber
      },
      set(value) {
        this.property.landRegisterNumber = value
      }
    },
    totalPropertyValueQuota() {
      return this.formatValueQuota(this.valuesQuota.reduce((accumulator, object) => {
        return accumulator + object.valueQuota
      }, 0))
    },
    employeeList() {
      return (this.$store.getters['employee/getEmployees'] || []).filter(
        e => !e.deleted
      )
    },
    propertyAssignee: {
      get() {
        if (this.isAdminModule) {
          return this.property.assignees.contactIdAssigneeAdmin
        }
        if (this.isAccountingModule) {
          return this.property.assignees.contactIdAssigneeAccounting
        }
        if (this.isAssetModule) {
          return this.property.assignees.contactIdAssigneeAsset
        }
        if (this.isOwnerModule) {
          return this.property.assignees.contactIdAssigneeOwner
        }
        return ''
      },
      set(value) {
        if (this.isAdminModule) {
          this.property.assignees.contactIdAssigneeAdmin = value
        }
        if (this.isAccountingModule) {
          this.property.assignees.contactIdAssigneeAccounting = value
        }
        if (this.isAssetModule) {
          this.property.assignees.contactIdAssigneeAsset = value
        }
        if (this.isOwnerModule) {
          this.property.assignees.contactIdAssigneeOwner = value
        }
      }
    }
  },
  watch: {
    property: {
      handler: function (newValue) {
        if (!newValue.opted) {
          newValue.optedPortionKey = ''
        }
        if (this.loadingFinish) {
          this.$emit('has-change', true)
        }
      },
      deep: true
    },
    'property.name': function (newValue) {
      // it is possible to copy paste more than 50 characters in Property name even if maxLength validation is set to 50
      if (newValue && newValue.length > 50) {
        this.property.name = newValue.substring(0, 50)
      }
    },
    mortgages: {
      handler: function (newVal, oldVal) {
        if (this.loadingFinish && (oldVal.length !== 0 || (newVal.length > 0 && !newVal[newVal.length - 1].id))) {
          this.$emit('has-change', true)
        }
      },
      deep: true
    },
    openedCollapses: function (newVal) {
      this.renderDevices = newVal.includes('collapse-property-device')
    },
    renderDevices: function (newVal) {
      if (newVal === true) {
        this.$nextTick(function () {
          this.renderDevicesAnimation = false
        })
      }
    },
    devices: {
      handler: function (newVal) {
        if (this.loadingFinish && newVal.length > 0 && !newVal[newVal.length - 1].id) {
          this.$emit('has-change', true)
        }
      },
      deep: true
    },
    loadingFinish: function (newValue) {
      if (newValue) {
        let scrollpos = localStorage.getItem('scrollpos')
        if (scrollpos) window.scrollTo(0, parseInt(scrollpos))
      }
    }
  },
  mounted() {
    this.checkAicEnabled()
    this.propertyInsuranceCollapseOpen = this.openedCollapses.includes('collapse-property-insurance') || this.expandPropertyInsuranceId !== ''
    if (localStorage.getItem('deviceIdNewDocument')) {
      this.deviceIdNewDocument = localStorage.getItem('deviceIdNewDocument')
    }
    if (localStorage.getItem('insuranceIdNewDocument')) {
      this.insuranceIdNewDocument = localStorage.getItem('insuranceIdNewDocument')
    }
    if (localStorage.getItem('mortgagePropertyIdNewDocument')) {
      this.mortgageIdNewDocument = localStorage.getItem('mortgagePropertyIdNewDocument')
    }
    if (localStorage.getItem('propBankIndexNewDocument')) {
      this.propBankIndexNewDocument = parseInt(localStorage.getItem('propBankIndexNewDocument'))
    }
    if (localStorage.getItem('addGeneralPropertyDocument') === 'true') {
      this.addDocumentClicked()
    }
    document.body.onmousedown = function(e) {
      if (e.button === 1) {
        e.preventDefault()
        return false
      }
    }
    this.getBuildingList()
    this.bankDetails = this.propertySelected.bankInfo || []
    this.initRentComponents = this.property.flatRateRentComponents.concat(this.property.advancePaymentRentComponents).concat(this.property.additionalRentComponents).flatMap(x => x.rentComponent)
    this.property.optedPortionKey = this.propertySelected.optedPortionKey !== '' && this.propertySelected.optedPortionKey !== 'OPTED_PORTION_KEY_UNDEFINED' && this.propertySelected.optedPortionKey !== null ? this.propertySelected.optedPortionKey : ''
    this.property.type = this.propertySelected.type !== '' && this.propertySelected.type !== 'PROPERTY_TYPE_UNDEFINED' && this.propertySelected.type !== null ? this.propertySelected.type : ''
    if (this.property.keyDateServiceChargeSettlement) {
      const keydate = this.$options.filters.objectToDateInput(this.property.keyDateServiceChargeSettlement)
      const keyDateArrayD = keydate.split('-')
      this.dates.keyDateServiceChargeSettlement = keyDateArrayD[2] + '.' + keyDateArrayD[1]
    }
    if (this.property.documentIds.length > 0) {
      MediaApi.listDocuments(this.property.documentIds)
        .then(response => {
          this.propertyDocuments = response.documents
        })
    }
    if (this.property.imageMediaId !== '') {
      MediaApi.getMediaByID(this.property.imageMediaId)
        .then(response => {
          this.propertyImageMedia = response.media
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
        })
    }

    this.devicesLoading = true
    ObjectApi.getDevicesByReferenceIds([this.property.id])
      .then(response => {
        this.devices = response.devices.sort(
          function(a, b) {
            if (a.uninstallation === b.uninstallation) {
              return a.numericId > b.numericId ? 1 : -1
            }
            return a.uninstallation === null ? -1 : 1
          })
        this.devicesLoading = false
        this.$nextTick(() => {
          this.$emit('has-change', false)
        })
      })
    this.renderDevices = this.openedCollapses.includes('collapse-property-device')
    this.mortgagesLoading = true
    ObjectApi.getMortgagesByReferenceIds([this.property.id])
      .then(response => {
        this.mortgages = this.sortByDateMortgages(response.mortgages)
        this.mortgagesLoading = false

        // Load documents of mortgages
        let documentIds = []
        for (const mortgage of response.mortgages) {
          if (mortgage.documentIds.length > 0) {
            documentIds = documentIds.concat(mortgage.documentIds)
          }
        }
        if (documentIds.length > 0) {
          MediaApi.listDocuments(documentIds)
            .then(documents => {
              this.mortgageDocuments = documents
            })
        }
        this.$nextTick(() => {
          this.$emit('has-change', false)
        })
      })
    this.insurancesLoading = true
    ObjectApi.getInsurancesByReferenceId([this.property.id])
      .then(response => {
        this.insurancesLoading = false
        this.$nextTick(() => {
          this.$emit('has-change', false)
        })
        this.propertyInsurances = this.sortByDateInsurances(response.insurances)

        // Load documents of insurances
        let documentIds = []
        for (const insurance of response.insurances) {
          if (insurance.documentIds.length > 0) {
            documentIds = documentIds.concat(insurance.documentIds)
          }
        }

        if (documentIds.length > 0) {
          MediaApi.listDocuments(documentIds)
            .then(documents => {
              this.insurancesDocuments = documents
            })
        }
      })
    ObjectApi.getDevicesByReferenceIds([this.property.id])
      .then(response => {
        this.devices = response.devices.sort(
          function(a, b) {
            if (a.uninstallation === b.uninstallation) {
              return a.numericId > b.numericId ? 1 : -1
            }
            return a.uninstallation === null ? -1 : 1
          })
        this.insurancesLoading = false
        this.$nextTick(() => {
          this.$emit('has-change', false)
        })
      })
    ContactApi.getContacts(this.property.ownerId)
      .then(contacts => {
        this.propertyOwnerContacts = contacts.persons.concat(contacts.companies)
      })
      .catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
      })
    ContactApi.getContacts(this.$store.getters['user/getAccountId'])
      .then(response => {
        const allContacts = response.persons.concat(response.companies)
        const listEmp = allContacts.filter(con => con.types.includes('EMPLOYEE'))
        this.listEmployees = listEmp.filter(contact => {
          const matchingEmployee = this.employeeList
            .filter(employee => !employee.blocked)
            .find(employee => employee?.email?.toLowerCase()?.trim() === contact?.email?.toLowerCase()?.trim())

          return matchingEmployee !== undefined && matchingEmployee !== null
        }).sort((a, b) => {
          const nameA = a.firstName && a.lastName ? a.firstName + ' ' + a.lastName : a.name
          const nameB = b.firstName && b.lastName ? b.firstName + ' ' + b.lastName : b.name
          if (nameA > nameB) {
            return 1
          }
          if (nameA < nameB) {
            return -1
          }
          return 0
        })
      })
      .catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
      })

    if (this.expandPropertyMortgageId !== '') {
      setTimeout(() => {
        const mortgageId = 'property_mortgage_' + this.expandPropertyMortgageId
        const element = document.getElementById(mortgageId)
        element.scrollIntoView(true)
        window.scrollBy(0, -50)
      }, 700)
    }
    if (this.expandPropertyInsuranceId !== '') {
      setTimeout(() => {
        const mortgageId = 'property_insurance_' + this.expandPropertyInsuranceId
        const element = document.getElementById(mortgageId)
        element.scrollIntoView(true)
        window.scrollBy(0, -50)
      }, 700)
    }
  },
  methods: {
    getAssigneeName() {
      const contact = this.listEmployees.find(contact => contact.id === this.propertyAssignee)
      if (contact) {
        return contact.firstName && contact.lastName ? contact.firstName + ' ' + contact.lastName : contact.name
      }
      return '-'
    },
    async addFile(event) {
      const files = await this.getFilesFromEvent(event)
      if (files && files.length > 0) {
        this.mediaUpload = files[0]
        if (['image/png', 'image/jpeg', 'image/jpg'].includes(this.mediaUpload.type)) {
          await this.sendFile()
        } else {
          this.mediaUpload = null
          Vue.toasted.show(this.$tc('message.savingErrors.fileNotSupported'), { type: 'error' })
        }
      }
    },
    async sendFile() {
      this.mediaUploadProcessing = true
      const selectedFile = this.mediaUpload ? [this.mediaUpload] : []
      const response = await this.uploadMedia(selectedFile, this.property.ownerId, true)
      if (response?.length > 0) {
        const media = response[0]
        this.propertyImageMedia = media
        this.property.imageMediaId = media.id
        this.mediaUpload = null
      }
      this.mediaUploadProcessing = false
    },
    getBuildingList() {
      this.isBusy = true
      if (this.propertySelected.buildingIds.length > 0) {
        ObjectApi.getBuildingsListByIds(this.propertySelected.buildingIds)
          .then(async response => {
            this.buildingList = response.buildings
            if (this.propertySelected?.type === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP') {
              this.getValueQuota()
            }
          })
      }
    },
    getValueQuota() {
      for (let i = 0; i < this.buildingList.length; i++) {
        const buildingId = this.buildingList[i].id
        ObjectApi.getTotalValueQuotaForBuilding(buildingId)
          .then(response => {
            const obj = { buildingId: buildingId, valueQuota: response.totalValueQuota }
            this.valuesQuota.push(obj)
          })
      }
    },
    downloadAllDocuments(e) {
      e.preventDefault()
      const idsAndNames = this.propertyDocuments.map(x => ({ id: x.versions[x.versions.length - 1].mediaId, mediumName: x.name }))
      const nameOfMergedFile = this.$tc('message.propertiesView.documents') + ' - ' + this.propertySelected.name
      this.mergeDownloadAllDocuments(idsAndNames, nameOfMergedFile)
    },
    goToEditMode() {
      localStorage.setItem('addGeneralPropertyDocument', 'true')
      this.$router.push({
        name: this.moduleRoutePrefix + 'PropertyEditingView',
        params: { id: this.$route.params.id },
        query: {
          view: 'property'
        }
      })
    },
    showImage() {
      if (this.readOnly && this.propertyImageMedia && this.propertyImageMedia.url) {
        this.galleryImages = [
          this.propertyImageMedia.url
        ]
        this.$nextTick(function () {
          this.$viewerApi({
            images: this.galleryImages,
            options: this.viewerOptions
          })
        })
      }
    },
    cityChanged(event) {
      this.property.address.city = event.city
      this.property.address.state = event.state
    },
    async getCityFromZip() {
      const zip = String(this.property.address.zip ?? '')
      if (zip.length > 3 && this.property.address.country === 'CH') {
          const zipData = await this.getZipInfo(zip)
          if (zipData) {
            this.property.address.state = zipData.state
            if (zipData.cities.length > 1) {
              this.isCitiesSuggested = true
              this.citiesValue = zipData.cities
            } else {
              this.isCitiesSuggested = false
              this.$nextTick(() => {
                this.property.address.city = zipData.cities[0].city
              })
            }
          }
      }
    },
    duplicateDevice(device) {
      const duplicatedDevice = copyObject(device)
      delete duplicatedDevice.id
      duplicatedDevice.internalId = Date.now()
      duplicatedDevice.isNewItem = true
      duplicatedDevice.documentIds = []
      this.devices.push(duplicatedDevice)
      this.$nextTick(() => {
        this.$emit('has-change', true)
        const indexDevice = this.devices.length - 1
        const domRect = this.$el.querySelector('#device-property-' + indexDevice)
        if (domRect) {
          domRect.scrollIntoView(true)
          this.$el.querySelector('#collapse-device-property-' + indexDevice + ' input').focus()
        }
      })
    },
    uploadImageClicked() {
      if (!this.readOnly) {
        const btn = this.$refs.mediaUpload.$el.children
        btn[0].click()
      }
    },
    addDocumentClicked(suffix = '') {
      localStorage.removeItem('addGeneralPropertyDocument')
      this.$bvModal.show('add-document-modal' + suffix)
    },
    documentCreated(document) {
      this.property.documentIds.push(document.id)
      this.propertyDocuments.push(document)
    },
    updateMortgage(mortgage) {
      const index = this.mortgages.findIndex(mo => mo.id === mortgage || mo.internalId === mortgage)
      if (index >= 0) {
        if (this.mortgages[index].id !== undefined) {
          this.$emit('has-change', true)
        }
      }
    },
    updateBankDetails(mortgage) {
      const index = this.bankDetails.findIndex(bd => bd.id === mortgage || bd.internalId === mortgage)
      if (index >= 0) {
        if (this.bankDetails[index].id !== undefined) {
          this.$emit('has-change', true)
        }
      }
    },
    versionAdded(updatedDocument) {
      this.propertyDocuments.forEach((doc, index) => {
        if (doc.id === updatedDocument.id) {
          this.propertyDocuments.splice(index, 1, updatedDocument)
        }
      }, this)
    },
    deleteDocument(document) {
      this.$emit('has-change', true)
      this.property.documentIds.forEach((doc, index) => {
        if (doc === document.id) {
          this.property.documentIds.splice(index, 1)
        }
      }, this)

      const index = this.propertyDocuments.findIndex(doc => doc.id === document.id)
      if (index >= 0) {
        this.$delete(this.propertyDocuments, index)
      }
    },
    deleteMedia() {
      this.propertyImageMedia = null
      this.property.imageMediaId = ''
    },
    clearkeyDateServiceChargeSettlementDate() {
      this.keyDateServiceChargeSettlement = null
      this.dates.keyDateServiceChargeSettlement = null
    },
    handleKeyDateChange() {
      if (!this.dates.keyDateServiceChargeSettlement) {
        this.property.keyDateServiceChargeSettlement = null
        return
      }

      const parts = this.dates.keyDateServiceChargeSettlement.split('.')
      if (parts.length !== 2) {
        this.property.keyDateServiceChargeSettlement = null
        return
      }

      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)

      if (isNaN(day) || isNaN(month) ||
          day < 1 || day > 31 ||
          month < 1 || month > 12) {
        this.property.keyDateServiceChargeSettlement = null
        return
      }

      const currentYear = new Date().getFullYear()
      const dateObj = new Date(currentYear, month - 1, day)

      if (dateObj.getDate() !== day || dateObj.getMonth() !== month - 1) {
        this.property.keyDateServiceChargeSettlement = null
        return
      }

      this.property.keyDateServiceChargeSettlement = this.toObject(dateObj)
    },
    checkInputValue($event) {
      const allowedKeys = [8, 9, 46, 37, 39]
      const isNumber = ($event.keyCode >= 48 && $event.keyCode <= 57) ||
                       ($event.keyCode >= 96 && $event.keyCode <= 105)

      if (isNumber || allowedKeys.includes($event.keyCode)) {
        const input = $event.target
        const hasSelection = input.selectionStart !== input.selectionEnd

        if (this.dates.keyDateServiceChargeSettlement?.length >= 5 &&
            !allowedKeys.includes($event.keyCode) &&
            !hasSelection) {
          $event.preventDefault()
        }
        return true
      } else if ($event.key === '.') {
        return true
      } else {
        $event.preventDefault()
        return false
      }
    },
    mountEventListenersKeyDate(e) {
      if (!e) return

      // Remove any non-digit and non-slash characters
      const cleanedInput = e.replace(/[^\d/]/g, '')

      let formattedInput = cleanedInput

      // Auto-add slash after 2 digits if not already present
      if (formattedInput.length === 2 && !formattedInput.includes('.')) {
        formattedInput += '.'
      }

      // Insert slash after first 2 digits if we have more than 2 digits
      if (formattedInput.length > 2 && !formattedInput.includes('.')) {
        formattedInput = formattedInput.substring(0, 2) + '.' + formattedInput.substring(2)
      }

      // Limit to 5 characters (dd/mm format)
      if (formattedInput.length > 5) {
        formattedInput = formattedInput.substring(0, 5)
      }

      // Update the input value if it changed
      if (formattedInput !== e) {
        this.$nextTick(() => {
          this.keyDateService = formattedInput
        })
      }

      // Store the current value for next comparison
      this.dates.keyDateServiceChargeSettlement = formattedInput

      // Process the date if it's complete enough
      if (formattedInput.length >= 4) {
        this.handleKeyDateChange()
      }
    },
    addNewMortgage() {
      const newMortgage = JSON.parse(JSON.stringify(emptyMortgage))
      newMortgage.internalId = Date.now()
      newMortgage.ownerId = this.property.ownerId
      newMortgage.referenceId = this.property.id
      this.mortgages.push(newMortgage)
    },
    addNewBankDetails() {
      this.bankDetails.push({
        internalId: Date.now(),
        ownerId: this.property.ownerId,
        referenceId: this.property.id,
        documentIds: [],
        isNewItem: true,
        bankName: '',
        clearingNumber: '',
        bic: '',
        address: {
          city: '',
          country: '',
          street: '',
          zip: ''
        },
        validFrom: null,
        expiresAt: null,
        remark: ''
      })
    },
    addNewInsurance() {
      const insurance = {
        internalId: Date.now(),
        ownerId: this.property.ownerId,
        referenceId: this.property.id,
        documentIds: [],
        isNewItem: true,
        insuranceCompany: '',
        type: '',
        benefits: [],
        notes: '',
        amount: -1,
        yearlyCosts: -1,
        excess: -1,
        premiumPaidUntil: null,
        indexYear: null
      }
      this.propertyInsurances.push(insurance)
      this.$nextTick(() => {
        this.$emit('has-change', true)
        const indexInsurance = this.propertyInsurances.length - 1
        const domRect = this.$el.querySelector('#insurance-i-' + indexInsurance)
        if (domRect) {
          domRect.scrollIntoView(true)
          this.$el.querySelector('#insurance-i-' + indexInsurance + ' input').focus()
        }
      })
    },
    addNewDevice() {
      const device = JSON.parse(JSON.stringify(helper.emptyDevice))
      device.internalId = Date.now() + Math.floor(Math.random() * 1000)
      device.ownerId = this.property.ownerId
      device.referenceId = this.property.id
      // this.devices.push(device)
      Vue.set(this.devices, this.devices.length, device)
      this.$nextTick(() => {
        const indexDevice = this.devices.length - 1
        const domRect = this.$el.querySelector('#device-i-' + indexDevice)
        if (domRect) {
          domRect.scrollIntoView(true)
          this.$el.querySelector('#device-i-' + indexDevice + ' input').focus()
        }
      })
    },
    removeMortgage(id) {
      const index = this.mortgages.findIndex(mo => mo.id === id || mo.internalId === id)
      if (index >= 0) {
        if (this.mortgages[index].id !== undefined) {
          this.deletedMortgageIds.push(this.mortgages[index].id)
        }
        this.$delete(this.mortgages, index)
        this.$emit('has-change', true)
      }
    },
    removeBankDetails(id) {
      const index = this.bankDetails.findIndex(mo => mo.id === id || mo.internalId === id)
      if (index >= 0) {
        if (this.bankDetails[index].id !== undefined) {
          this.deletedBankDetailsIds.push(this.mortgages[index].id)
        }
        this.$delete(this.bankDetails, index)
        this.$emit('has-change', true)
      }
    },
    removeDevice(id) {
      if (id !== undefined) {
        ObjectApi.deleteDevice(this.devices[id].id).then(() => {
          Vue.toasted.show(this.$t('message.successMessages.deviceDeleted'), { type: 'success' })
        })
      }
      this.$delete(this.devices, id)
    },
    removeInsurance(id) {
      const index = this.propertyInsurances.findIndex(ins => ins.id === id || ins.internalId === id)
      if (index >= 0) {
        if (this.propertyInsurances[index].id !== undefined) {
          ObjectApi.deleteInsurance(this.propertyInsurances[index].id)
        }
        this.$delete(this.propertyInsurances, index)
      }
      this.$emit('has-change', true)
    },
    openActivities(device) {
      if (device !== null && device !== undefined) {
        this.selectedDevice = device
        this.$emit('selected-device-for-history', device)
      }
    },
    openActivitiesMiddle(device) {
      let routeData = ''
      if (device !== null && device !== undefined) {
        if (this.isOwnerModule) {
          routeData = this.$router.resolve({
            name: 'OwnerPropertyDetailsView',
            params: { id: this.property.id },
            query: {
              view: 'history',
              elementSelected: device.id
            }
          })
        } else if (this.isAdminModule) {
          routeData = this.$router.resolve({
            name: 'AdminPropertyDetailsView',
            params: { id: this.property.id },
            query: {
              view: 'history',
              elementSelected: device.id
            }
          })
        } else if (this.isAssetModule) {
          routeData = this.$router.resolve({
            name: 'AssetPropertyDetailsView',
            params: { id: this.property.id },
            query: {
              view: 'history',
              elementSelected: device.id
            }
          })
        } else if (this.isAccountingModule) {
          routeData = this.$router.resolve({
            name: 'AccountingPropertyDetailsView',
            params: { id: this.property.id },
            query: {
              view: 'history',
              elementSelected: device.id
            }
          })
        }
      }
      window.open(routeData.href, '_blank')
    },
    async saveData() {
      this.$v.property.$touch()
      let valid = true
      let validDevice = true
      const errorArray = []

      if (this.dates.keyDateServiceChargeSettlement) {
        if (typeof this.property.keyDateServiceChargeSettlement === 'string' ||
            !this.property.keyDateServiceChargeSettlement ||
            !this.property.keyDateServiceChargeSettlement.year) {
          errorArray.push(`${this.$t('message.savingErrors.invalidDate')}: ${this.$t('message.propertiesView.keyDate')}`)
          valid = false
        }
      }

      if (this.$refs['property-mortgage']) {
        this.$refs['property-mortgage'].forEach(element => {
          if (element.isInvalid()) {
            if (!element.isCollapseOpen() && (this.$refs.collapseMortgage && !this.$refs.collapseMortgage.visible)) {
              errorArray.push(this.$t('message.savingErrors.validationError') + this.$t('message.onBoarding.createBuilding.mortgage') + this.$t('message.savingErrors.validationError2'))
            }
            valid = false
          }
        })
      }
      if (this.$refs['property-insurance']) {
        this.$refs['property-insurance'].forEach(element => {
          if (element.isInvalid()) {
            if (!element.isCollapseOpen()) {
              errorArray.push(this.$t('message.savingErrors.validationError') + this.$t('message.onBoarding.createBuilding.insurance') + this.$t('message.savingErrors.validationError2'))
            }
            valid = false
          }
        })
      }
      if (this.$refs['property-device']) {
        this.$refs['property-device'].forEach(element => {
          if (element.isInvalid()) {
            if (!element.isCollapseOpen()) {
              errorArray.push(this.$t('message.savingErrors.validationError') + this.$t('message.onBoarding.devices.title') + this.$t('message.savingErrors.validationError2'))
            }
            validDevice = false
          }
        })
      }
      if (this.$refs['property-bank-details']) {
        this.$refs['property-bank-details'].forEach(element => {
          if (element.isInvalid()) {
            if (!element.isCollapseOpen() && (this.$refs.collapseBankDetails && !this.$refs.collapseBankDetails.visible)) {
              errorArray.push(this.$t('message.savingErrors.validationError') + this.$t('message.onBoarding.createBuilding.propertyBankDetails') + this.$t('message.savingErrors.validationError2'))
            }
            valid = false
          }
        })
      }
      if (errorArray.length > 0) {
        this.showManyToast(errorArray)
      }

      if (!this.$v.property.$invalid && valid && validDevice) {
        await this.$store.dispatch('onboarding/setOpenedCollapses')
        this.$emit('set-loading', true)
        const promises = []
        for (let index = 0; index < this.deletedMortgageIds.length; index++) {
          promises.push(ObjectApi.deleteMortgage(this.deletedMortgageIds[index]))
        }
        this.property.bankinfo = this.bankDetails

        const serverRes = await ObjectApi.getPropertyListByIds([this.property.id])
        const serverProp = Array.isArray(serverRes.properties) ? serverRes.properties[0] : serverRes.properties

        const toIdArray = (prop) => {
          if (!prop) return []
          return Array.from(new Set(prop.buildingIds || []))
        }

        const clientIds = toIdArray(this.property)
        const serverIds = toIdArray(serverProp)

        const sameSet = (a, b) =>
          a.length === b.length && a.every(id => b.includes(id))

         if (!sameSet(clientIds, serverIds)) {
          this.property.buildingIds = serverIds
        }
        // update property
        promises.push(ObjectApi.updateProperty(this.property))
        if (this.mortgages.length > 0 && !this.isJanitorEditor) {
          for (const mortgage of this.mortgages) {
            if (mortgage.internalId) {
              promises.push(ObjectApi.createMortgage(mortgage))
            } else {
              promises.push(ObjectApi.updateMortgage(mortgage.id, mortgage))
            }
          }
        }
        if (this.devices.length > 0) {
          for (const device of this.devices) {
            if (device.internalId) {
              promises.push(ObjectApi.createDevice(device))
            } else {
              promises.push(ObjectApi.updateDevice(device))
            }
          }
        }
        if (this.propertyInsurances.length > 0 && !this.isJanitorEditor) {
          for (const insurance of this.propertyInsurances) {
            if (insurance.internalId) {
              promises.push(ObjectApi.createInsurance(insurance))
            } else {
              promises.push(ObjectApi.updateInsurance(insurance))
            }
          }
        }

        return Promise.all(promises)
          .then((response) => {
            // update the assignee after finish updating property
            const promiseSetAssignee = []
            if (this.isAdminModule) {
              promiseSetAssignee.push(ObjectApi.setAssigneePropertyAdmin(this.property.assignees.contactIdAssigneeAdmin, [this.property.id]))
            }
            if (this.isAccountingModule) {
              promiseSetAssignee.push(ObjectApi.setAssigneePropertyAccounting(this.property.assignees.contactIdAssigneeAccounting, [this.property.id]))
            }
            if (this.isAssetModule) {
              promiseSetAssignee.push(ObjectApi.setAssigneePropertyAsset(this.property.assignees.contactIdAssigneeAsset, [this.property.id]))
            }
            if (this.isOwnerModule) {
              promiseSetAssignee.push(ObjectApi.setAssigneePropertyOwner(this.property.assignees.contactIdAssigneeOwner, [this.property.id]))
            }
            Promise.all(promiseSetAssignee)
            const newAddedDevice = this.devices.filter(x => x.internalId)
            if (response.filter(x => x.device).length > 0) {
              this.devices = []
              response.forEach(element => {
                if (element.device) {
                  this.devices.push(element.device)
                }
              })
              this.devices = this.devices.sort(
                function(a, b) {
                  if (a.uninstallation === b.uninstallation) {
                    return a.numericId > b.numericId ? 1 : -1
                  }
                  return a.uninstallation === null ? -1 : 1
                })
            }
            if (response.filter(x => x.insurance).length > 0) {
              const insurances = []
              response.forEach(element => {
                if (element.insurance) {
                  insurances.push(element.insurance)
                }
              })
              this.propertyInsurances = this.sortByDateInsurances(insurances)
            }
            if (response.filter(x => x.mortgage).length > 0) {
              this.mortgages = []
              response.forEach(element => {
                if (element.mortgage) {
                  this.mortgages.push(element.mortgage)
                }
              })
            }
            this.devices = this.devices.sort(
              function(a, b) {
                if (a.uninstallation === b.uninstallation) {
                  return a.numericId > b.numericId ? 1 : -1
                }
                return a.uninstallation === null ? -1 : 1
              })
            Vue.toasted.show(this.$t('message.successMessages.propertyCreation'), { type: 'success' })
            if (newAddedDevice.length > 0) {
              Vue.toasted.show(this.$t('message.successMessages.deviceSaved'), { type: 'success' })
            }
          })
          .catch(e => {
            if (e.response && e.response.data && e.response.data.code === 9) {
              Vue.toasted.show(this.$t('message.savingErrors.propertyWithOptedTenancies'), { type: 'error' })
            } else if (e.response && e.response.data && e.response.data.code === 3) {
              const component = []
              const current = this
              this.initRentComponents.forEach(c => {
                if (e?.response?.data?.error?.indexOf(c) !== -1) {
                  if (c.indexOf('FLAT_RATE_RENT') !== -1) {
                    component.push(current.$t('message.propertiesView.rentComponents.flatRateRentComponents.' + c))
                  } else if (c.indexOf('ADDITIONAL_RENT') !== -1) {
                    component.push(current.$t('message.propertiesView.rentComponents.additionalRentComponents.' + c))
                  } else if (c.indexOf('ADVANCE_PAYMENT_RENT_COMPONENT') !== -1) {
                    component.push(current.$t('message.propertiesView.rentComponents.advancePaymentRentComponents.' + c))
                  }
                }
              })
              Vue.toasted.show(this.$t('message.savingErrors.rentComponentStillInUse', { component: component.join(', ') }), { type: 'error' })
            } else {
              Vue.toasted.show(this.$t('message.savingErrors.property'), { type: 'error' })
            }
          })
          .finally(() => {
            this.$emit('set-loading', false)
          })
      } else {
        this.scrollToError()
      }
    },
    async updateProperty() {
      const serverRes = await ObjectApi.getPropertyListByIds([this.property.id])
      const serverProp = Array.isArray(serverRes.properties) ? serverRes.properties[0] : serverRes.properties

      const toIdArray = (prop) => {
        if (!prop) return []
        return Array.from(new Set(prop.buildingIds || []))
      }

      const clientIds = toIdArray(this.property)
      const serverIds = toIdArray(serverProp)

      const sameSet = (a, b) =>
        a.length === b.length && a.every(id => b.includes(id))

      if (!sameSet(clientIds, serverIds)) {
        this.property.buildingIds = serverIds
      }
      return ObjectApi.updateProperty(this.property).catch(() => {
        Vue.toasted.show(this.$tc('message.savingErrors.property'), { type: 'error' })
      })
    },
    handleDragStart(itemId) {
      // Set the dragged item's ID in the data transfer object
      event.dataTransfer.setData('itemId', itemId)
    }
  },
  validations() {
    const validationProperty = {
      property: {
        name: {
          required,
          maxLength: maxLength(50)
        },
        plotArea: {
          minValue: minValue(-1),
          decimal
        },
        opted: {
          required
        },
        address: {
          zip: {
            required,
            isZipValid: isZipValid(this.property.address)
          },
          city: {
            required
          }
        }
      }
    }
    if (this.property.opted) {
      validationProperty.property.optedPortionKey = {
        required
      }
    }
    return validationProperty
  }
}
</script>
<style lang="scss" scoped>
.building-image {
  max-height: 200px;
  max-width: 100%;
}

.object-image-remove-container {
  position: absolute;
  z-index: 2;
  width: 16px;
  height: 16px;
  margin: 4px 12px;
}
.object-image-remove {
  position: absolute;
  z-index: 3;
  width: 17px;
  height: 16px;
  color: red;
  &.bg {
    left: 0;
    top: -3px;
    width: 14px;
    height: 22px;
    color: white;
  }
}
</style>
